import { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import DateSelector from "../../components/shared/fields/DateSelector";
import Datatable from "../../components/shared/datatable/Datatable";
import Button from "../../components/shared/buttons/button";
import GridCard from "../../components/shared/gridCard/GridCard";
import useTranslator from "../../hooks/useTranslator";
import { convertToUserTimezone, formatDateAndTime, formatNumber, getCurrentDate, makePriorLotNumber } from "../../helpers/Utils";
import { DEFAULT_DATETIME_FORMAT, PROJECT_DATE_FORMAT } from "../../helpers/constants";
import { useGetPackingPendingQuery, useGetPastingPackingLogQuery, useGetPastingPackingSummaryQuery, useGetPastingPackingWasteQuery } from "./mixingApi";
import KgGreen from "../../images/kg_green.svg";
import PendingCase from "../../images/pendingCase.svg";
import KgRejected from "../../images/kg_rejected.svg";
import PastingModal from "./PastingModal";
import AddPastingModal from "./AddPastingModal";
import PackingWasteModal from "./PackingWasteModal";
import PackingPendingModal from "./PackingPendingModal";
import { PlusSvg } from "../../svgFiles/PlusSvg";

const Pasting = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate]           = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));
  const [page, setPage]           = useState(1);

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);

  const [showModal, setShowModal]               = useState(false);
  const [showAddModal, setShowAddModal]         = useState(false);
  const [showEditPendingModal, setShowEditPendingModal] = useState(false);
  const [showWasteModal, setShowWasteModal]     = useState(false);
  const [selectedRecord, setSelectedRecord]     = useState(null);
  const [modalMode, setModalMode]       = useState("edit");
  const [filter, setFilter]             = useState({});

  const [appliedFilters, setAppliedFilters] = useState({
    batch_code: "",
    date: "",
    status: "",
  });

  const handleFilterApply = (newFilters) => {
    setAppliedFilters(newFilters);
    setFilter(newFilters);
    setPage(1);
  };

  const pastingLogLimit = 10;
  const { data: logsData, isLoading, isFetching } = useGetPastingPackingLogQuery(
    { 
      page,
      per_page: pastingLogLimit,
      batch_code: lotNumber,
      ...filter
    },
    { 
      refetchOnMountOrArgChange: true
    }
  );

  // ############## Packing Waste ###############

  const waste_type = "packing_waste";
  const { data: existingWaste } = useGetPastingPackingWasteQuery(
    { 
      batch_code: lotNumber ,
      waste_type : waste_type
    },
    { skip: !lotNumber }
  );

  const { data: pendingMaterial } = useGetPackingPendingQuery(
    { 
      batch_code: lotNumber
    },
    { skip: !lotNumber }
  );
  

  const packingPendingRecord = pendingMaterial?.data?.find(
    (r) => r.material_key === "packing_material" && r.belongs_to === "packing"
  ) ?? null;

  const morningPackingWaste =
    existingWaste?.data?.find(
      item => item.shift === "morning" && item.waste_type === waste_type
    )?.weight_in_kg ?? 0;

  const eveningPackingWaste =
    existingWaste?.data?.find(
      item => item.shift === "evening" && item.waste_type === waste_type
    )?.weight_in_kg ?? 0;


  // ################## END ################

  const { data: summaryData } = useGetPastingPackingSummaryQuery(
    { batch_code: lotNumber },
    { skip: !lotNumber }
  );

  const listItems = [
    { 
      label: _t("total_kg_entered", "Total Kg entered"),
      value: formatNumber(summaryData?.data?.total_kg_entered ?? 0 ,0),
      icon: KgGreen
    },
    { 
      label: _t("total_boxes_packed","Total Boxes packed"),
      value: formatNumber(summaryData?.data?.total_boxes_packed ?? 0,0),
      icon: KgGreen
    },
    { 
      label: _t("total_kg_packed","Total Kg packed"),
      value: formatNumber(summaryData?.data?.total_kg_packed ?? 0 ,0),
      icon: KgGreen
    },
    { 
      label: _t("total_kg_not_packed", "Total Kg not packed"),
      value: formatNumber(summaryData?.data?.total_kg_not_packed ?? 0 ,0),
      icon: KgGreen
    },
    {
      label: _t("packing_pending_kg", "Packing Pending KG"),
      value: `${formatNumber(summaryData?.data?.pending_details?.material_unit ?? 0) } kg`,
      icon: KgGreen,
      extra_info: true,
      info_message:  `${summaryData?.data?.pending_details?.material_description ?? ''}`,
    },
    {
      label: _t("total_packing_waste", "Total Packing Waste"),
      value: `${formatNumber(summaryData?.data?.total_packing_waste ?? 0 ,0) } kg`,
      icon: KgGreen,
      // extra_info: true,
      // info_message:  `${summaryData?.data?.pending_details?.material_description ?? ''}`,
    },
  ];

  const handleEdit = (row) => {
    setSelectedRecord(row);
    setModalMode(row.status === "packed" ? "view" : "edit");
    setShowModal(true);
  };

  const columns = [
    { 
      label: _t("entered_date", "Entered Date"),
      accessor: "created_at",
      cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "" 
    },
    { 
      label: _t("shift","Shift"),
      accessor: "shift",
      cell: ({ value }) => value === "morning"
          ? _t("Morning" ,"Morning")
          : value === "evening"
          ? _t("Evening","Evening")
          : "--",
    },
    { 
      label: _t("packed_weight","Weight Packed"),
      accessor: "weight_packed",
      cell: ({ value }) => value ?? "--"
    },
    { 
      label: _t("total_boxes","No of Boxes"),
      accessor: "total_boxes_packed",
      cell: ({ value }) => value ?? "--"
    },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => (
        <span className={value === "packed" ? "cmn_badge" : "cmn_badge inactive"}>
          {value === "packed"  ? _t("status_packed",  "Packed")
           : value === "in_progress" ? _t("status_in_progress", "In Progress")
           : value}
        </span>
      ),
    },
    {
      label: _t("action", "Action"),
      accessor: "id",
      cell: ({ row }) => (
        <Button
          size="small"
          className={row.status === "packed" ? "borderRedBtn" : ""}
          label={row.status === "packed" ? _t("view", "View") : _t("edit", "Edit")}
          onClick={() => handleEdit(row)}
        />
      ),
    },
  ];

  return (
    <div>
      <PastingModal
        show={showModal}
        setShow={setShowModal}
        record={selectedRecord}
        mode={modalMode}
      />
      <AddPastingModal
        show={showAddModal}
        setShow={setShowAddModal}
        batchCode={lotNumber}
        record={selectedRecord}
        mode={modalMode}
      />
      {showWasteModal && (
        <PackingWasteModal
          show={showWasteModal}
          setShow={setShowWasteModal}
          batchCode={lotNumber}
        />
      )}
      {showEditPendingModal && (
        <PackingPendingModal
          show={showEditPendingModal}
          setShow={setShowEditPendingModal}
          batchCode={lotNumber}
        />
      )}

      {/* Overview tiles */}
      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("packing_logs", "Packing Logs")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              day={currentDate === date ? _t("common_today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)}
              extra_info={list.extra_info}
              info_message={list.info_message}
              showDay = {list.showDay}
            />
          ))}
        </ul>
      </div>

      {/* Two-column layout: table col-9, right panel col-3 */}
      <div className="d-flex gap-2">
        {/* ── Left: Pasting logs table ── */}
        <Col lg={9}>
          <Datatable
            title={_t("packing_logs", "Packing Logs")}
            columns={columns}
            tableData={logsData?.data}
            isLoading={isLoading || isFetching}
            buttons={date === currentDate ? [
              {
                label: _t("add_material", "Add Material"),
                onClick: () =>{
                  setShowAddModal(true);
                  setModalMode('add');
                },
                icon: PlusSvg,
              },
            ] : []}
            showPegination={true}
            page={page}
            onPageChange={setPage}
            showCount={true}
            pageLimit={pastingLogLimit}
            showFilterButton={false}
            showFilter={handleFilterApply}
            filterModule="pasting_filter"
            filters={appliedFilters}
          />
        </Col>

        {/* ── Right: Pending Pasting + Waste Material Log ── */}
        <Col lg={3}>
          {/* Pending Pasting */}
          <div className="overview mb-3">
            <div className="d-flex gap-2 align-items-center pb-4">
              <h5 className="flex-grow-1 mb-0">{_t("pending_packing", "Pending Packing")}</h5>
            </div>
            <ul className="overview cmn_card m-0 grid_sorting_wrapper">
              <GridCard
                title={_t("pending_packing", "Pending Packing")}
                value={`${packingPendingRecord?.material_unit ?? 0} kg`}
                icon={PendingCase}
                showEdit={date === currentDate}
                onEdit={() => setShowEditPendingModal(true)}
              />
            </ul>
          </div>

          {/* Waste Material Log */}
          <div className="overview cmn_card mb-3">
            <div className="overview_header d-flex gap-2 align-items-center p-3">
              <div className="flex-grow-1">
                <h6 className="mb-0">{_t("waste_material_log", "Waste Material Log")}</h6>
              </div>
            </div>

            <div className="p-2">

              {/* Packing Waste */}
              <div className="rejected-card">
                <div className="d-flex align-items-center gap-3">
                  <div className="rejected-icon">
                    <img src={KgRejected} alt="Packing Waste" style={{ width: "48px", height: "48px" }} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0" style={{ fontSize: "14px", color: "#666" }}>
                        {_t("packing_waste", "Packing Waste")}
                      </h6>
                      {date === currentDate && 
                        <Button
                          size="xs"
                          label={_t("update", "Update")}
                          onClick={() => setShowWasteModal(true)}
                        />
                      }
                    </div>
                    <div className="d-flex gap-3">
                      <div className="rejected-shift-data">
                        <span style={{ fontSize: "12px", color: "#999" }}>{_t("morning", "Morning")}</span>
                        <h5 className="mb-0" style={{ fontSize: "20px", fontWeight: "600" }}>
                          { `${morningPackingWaste ?? 0} kg` }
                        </h5>
                      </div>
                      <div className="rejected-shift-data">
                        <span style={{ fontSize: "12px", color: "#999" }}>{_t("evening", "Evening")}</span>
                        <h5 className="mb-0" style={{ fontSize: "20px", fontWeight: "600", color: "#ee7225" }}>
                          { `${eveningPackingWaste ?? 0} kg`}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </div>
    </div>
  );
};

export default Pasting;
